/**
 * spl-pdp-example-gallery.js
 *
 * Manages the metaobject-driven product media gallery on the PDP.
 * Works in conjunction with product-media-gallery.liquid (B+C hybrid approach):
 *
 *   - Selected variant on page load: full <media-gallery> rendered and visible.
 *   - All other variants: rendered inside <template> tags, stamped into the DOM
 *     on first selection, then shown/hidden on subsequent selections.
 *
 * Responsibilities:
 *   1. Subscribe to FoxTheme.pubsub variantChange events (fired by product-info.js
 *      after fetch completes and variant is confirmed — guarantees correct variant ID).
 *   2. Stamp <template> content into the DOM on first selection of a variant.
 *   3. Show the active variant's .spl-gallery-instance, hide all others.
 *   4. Remove inert/aria-hidden from the active instance, restore on others.
 *   5. Destroy the previous Swiper instance and initialize the new one by
 *      calling MediaGallery.init() directly on the newly visible <media-gallery>.
 *      init() is called directly rather than relying on FoxTheme.Motion.inView,
 *      which only fires once per connectedCallback and won't re-fire on show/hide.
 *   6. patchSetActiveMedia() is applied as a safety measure on each swap.
 *      In practice it is not needed: setActiveMedia() in media-gallery.js already
 *      exits early when variant.featured_media is null, which is always the case
 *      for metaobject-driven variants. The patch prevents any future regression
 *      if that early-exit behavior changes.
 *
 * Event flow:
 *   FoxTheme.pubsub variantChange (fired after product-info.js fetch completes)
 *     → this.handleVariantChange({ data: { sectionId, variant } })
 *       → stampTemplate() if first visit to this variant
 *       → swapGallery()
 *         → destroyPreviousSwiper()
 *         → hideAll()
 *         → showInstance()
 *         → newGalleryEl.init() — direct call, bypasses inView
 *         → patchSetActiveMedia() — safety no-op patch
 *
 * DOM structure expected (rendered by product-media-gallery.liquid):
 *
 *   <!-- Selected variant (visible on load) -->
 *   <div class="spl-gallery-instance" data-spl-variant-gallery="{variantId}">
 *     <media-gallery id="MediaGallery-{sectionId}-{variantId}" data-spl-deferred="false">
 *       ...slides rendered from variant.metafields.custom.examples image lists...
 *     </media-gallery>
 *   </div>
 *
 *   <!-- Non-selected variants (inside <template> until first selection) -->
 *   <template data-spl-variant-template="{variantId}">
 *     <div class="spl-gallery-instance hidden" data-spl-variant-gallery="{variantId}"
 *          inert aria-hidden="true">
 *       <media-gallery id="MediaGallery-{sectionId}-{variantId}" data-spl-deferred="true">
 *         ...slides rendered from variant.metafields.custom.examples image lists...
 *       </media-gallery>
 *     </div>
 *   </template>
 *
 * Image source:
 *   Each variant's gallery slides are server-rendered from the variant's linked
 *   Examples metaobject (variant.metafields.custom.examples). The Examples metaobject
 *   contains up to 10 image lists (example_1 through example_10). The featured
 *   example is determined by the Designs metaobject override field for the product's
 *   pet type (e.g. dogs_featured_example_override). All example image lists are
 *   included as gallery slides, with the featured example's images appearing first.
 *   See product-media-gallery.liquid for the full resolution chain.
 *
 * Dependencies:
 *   - FoxTheme.pubsub (PUB_SUB_EVENTS.variantChange)
 *   - MediaGallery custom element (defined in vendor.js)
 *   - Swiper (via FoxTheme.Carousel, initialized inside MediaGallery.init())
 *
 * Placement:
 *   Loaded via <script src="{{ 'spl-pdp-example-gallery.js' | asset_url }}" defer>
 *   in sections/main-product.liquid. defer ensures it runs after DOM is ready
 *   and after vendor.js/theme.js have defined FoxTheme.pubsub and MediaGallery.
 */

(function () {
  'use strict';

  /**
   * SplPdpExampleGallery
   *
   * Controller class — one instance per product-info element on the page.
   * Scoped to a single section via sectionId to prevent cross-contamination
   * when multiple product-info elements exist (e.g. quick view + main PDP).
   */
  class SplPdpExampleGallery {
    constructor(productInfoEl) {
      /**
       * The <product-info> element this instance is scoped to.
       */
      this.productInfoEl = productInfoEl;

      /**
       * The section ID from data-section attribute on product-info.
       * Used to match variantChange events to the correct section and to
       * scope gallery container queries within the correct section wrapper.
       */
      this.sectionId = productInfoEl.dataset.section;

      /**
       * The .product__media-wrapper element containing all gallery instances.
       * All gallery DOM queries are scoped within this element.
       */
      this.galleryContainer = null;

      /**
       * The currently active variant ID (string).
       * Compared against incoming variantChange events to skip no-op swaps.
       */
      this.activeVariantId = null;

      /**
       * The previously active <media-gallery> element.
       * Stored so its Swiper instance can be destroyed before the next swap,
       * preventing stale Swiper references and memory leaks.
       */
      this.previousGalleryEl = null;

      /**
       * Unsubscribe function returned by FoxTheme.pubsub.subscribe.
       * Called in destroy() to remove the event subscription.
       */
      this.unsubscribeVariantChange = null;

      this.init();
    }

    /**
     * init()
     *
     * Finds the gallery container, reads the initially selected variant ID
     * from the visible gallery instance, patches setActiveMedia on the initial
     * gallery as a safety measure, and subscribes to variantChange events.
     *
     * Subscribes to variantChange (not optionValueSelectionChange) because:
     *   - optionValueSelectionChange fires on click, before the fetch completes.
     *     At that point [data-selected-variant] still holds the previous variant.
     *   - variantChange is published by product-info.js after the fetch completes
     *     and the variant is confirmed, with the resolved variant in the payload.
     *   This guarantees we always swap to the correct variant gallery.
     */
    init() {
      const section = this.productInfoEl.closest(
        `#shopify-section-${this.sectionId}`
      ) || document;

      this.galleryContainer = section.querySelector('.product__media-wrapper');

      if (!this.galleryContainer) {
        // No media wrapper found — product has no media gallery. Exit silently.
        return;
      }

      // Read the initially selected variant from the visible gallery instance.
      const initialInstance = this.galleryContainer.querySelector(
        '.spl-gallery-instance:not(.hidden)'
      );
      if (initialInstance) {
        this.activeVariantId = initialInstance.dataset.splVariantGallery;
        const initialGalleryEl = initialInstance.querySelector('media-gallery');
        if (initialGalleryEl) {
          this.previousGalleryEl = initialGalleryEl;
          // Safety patch: suppress setActiveMedia on the initial gallery.
          // setActiveMedia already exits early for metaobject variants (no featured_media),
          // but this patch prevents any future regression if that behavior changes.
          this.patchSetActiveMedia(initialGalleryEl);
        }
      }

      // Subscribe to variantChange — fired by product-info.js after fetch completes.
      // Payload: { data: { sectionId, html, variant } }
      this.unsubscribeVariantChange = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        this.handleVariantChange.bind(this)
      );
    }
    /**
     * handleVariantChange({ data: { sectionId, variant } })
     *
     * Called by FoxTheme.pubsub after product-info.js completes its fetch
     * and confirms the resolved variant. The payload includes the full variant
     * object with a guaranteed .id property.
     *
     * Matches events to this section via sectionId to prevent cross-contamination
     * when multiple product-info elements exist on the page simultaneously
     * (e.g. quick view modal open alongside the main PDP).
     *
     * Image source reminder:
     *   Each variant's gallery was server-rendered from the variant's linked
     *   Examples metaobject (variant.metafields.custom.examples). The gallery
     *   DOM already exists — either fully rendered (selected variant on load)
     *   or inside a <template> tag (all other variants). This handler's job
     *   is purely to stamp and show the correct pre-rendered gallery.
     *
     * @param {{ data: { sectionId: string, variant: object } }} payload
     */
    handleVariantChange({ data: { sectionId, variant } }) {
      // Only handle events for our section.
      if (sectionId !== this.sectionId) return;

      if (!variant?.id) return;

      const newVariantId = String(variant.id);

      // No-op if the same variant is already active.
      if (newVariantId === this.activeVariantId) return;

      // Check whether a gallery instance or template exists for this variant.
      // If neither exists, this variant has no SPL gallery — exit and let
      // product-info.js handle the unavailable/no-media state normally.
      const templateEl = this.galleryContainer.querySelector(
        `[data-spl-variant-template="${newVariantId}"]`
      );
      const existingInstance = this.galleryContainer.querySelector(
        `[data-spl-variant-gallery="${newVariantId}"]`
      );

      if (!templateEl && !existingInstance) return;

      // Stamp the template into the DOM if this is the first selection
      // of this variant. After stamping, the instance will be in the DOM
      // and swapGallery() will find it via [data-spl-variant-gallery].
      if (templateEl && !existingInstance) {
        this.stampTemplate(templateEl, newVariantId);
      }

      this.swapGallery(newVariantId);
    }

    /**
     * stampTemplate(templateEl, variantId)
     *
     * Clones the <template> content (a DocumentFragment) and inserts it into
     * the gallery container immediately after the template element, preserving
     * the original variant order from the Liquid loop.
     *
     * The template element is removed after stamping — it has served its purpose
     * and keeping it would cause future querySelector calls for
     * [data-spl-variant-template] to find a stale element.
     *
     * After stamping, the new .spl-gallery-instance is in the DOM but still
     * hidden and inert. swapGallery() handles showing it and calling init().
     *
     * image-lazy custom elements inside the stamped content will upgrade
     * automatically via their connectedCallback when appended to the DOM.
     * They use IntersectionObserver internally, so images only load when
     * the gallery becomes visible — no premature loading occurs.
     *
     * @param {HTMLTemplateElement} templateEl
     * @param {string} variantId - Used only for error logging.
     */
    stampTemplate(templateEl, variantId) {
      try {
        const fragment = templateEl.content.cloneNode(true);
        templateEl.after(fragment);
        templateEl.remove();
      } catch (e) {
        console.error(
          `[SplPdpExampleGallery] Failed to stamp template for variant ${variantId}.`,
          e
        );
      }
    }

    /**
     * swapGallery(newVariantId)
     *
     * Orchestrates the full gallery swap sequence:
     *
     *   1. Find the new .spl-gallery-instance and its <media-gallery> element.
     *   2. Destroy the previous Swiper instance (main slider + thumbs).
     *      Resetting previousGalleryEl.initialized = false allows re-init
     *      when that variant is selected again — init() will be called directly
     *      at that point, bypassing inView which won't re-fire on show/hide.
     *   3. Hide all instances (hidden class + inert + aria-hidden).
     *   4. Show the new instance (remove hidden + inert + aria-hidden).
     *   5. Call newGalleryEl.init() directly — bypasses FoxTheme.Motion.inView
     *      which only fires once per connectedCallback and won't re-fire after
     *      the element is shown. init() is guarded by this.initialized so it
     *      is safe to call on an already-initialized instance (no-op).
     *   6. Patch setActiveMedia as a safety measure.
     *   7. Update activeVariantId and previousGalleryEl.
     *
     * @param {string} newVariantId
     */
    swapGallery(newVariantId) {
      const newInstance = this.galleryContainer.querySelector(
        `[data-spl-variant-gallery="${newVariantId}"]`
      );

      if (!newInstance) {
        console.warn(
          `[SplPdpExampleGallery] No gallery instance found for variant ${newVariantId}.`
        );
        return;
      }

      const newGalleryEl = newInstance.querySelector('media-gallery');

      // Step 1: Destroy the previous Swiper instance before hiding.
      // Prevents stale Swiper references to hidden slides and memory leaks
      // from multiple simultaneously initialized Swiper instances.
      // destroySlider() sets this.sliderInstance = false internally,
      // allowing re-initialization when that variant is selected again.
      if (this.previousGalleryEl && this.previousGalleryEl !== newGalleryEl) {
        try {
          if (typeof this.previousGalleryEl.destroySlider === 'function') {
            this.previousGalleryEl.destroySlider();
          }
          // Destroy thumbs Swiper separately — destroySlider() only handles
          // the main viewer Swiper, not the thumbnails instance.
          if (
            this.previousGalleryEl.thumbsInstance &&
            typeof this.previousGalleryEl.thumbsInstance.slider?.destroy === 'function'
          ) {
            this.previousGalleryEl.thumbsInstance.slider.destroy();
            this.previousGalleryEl.thumbsInstance = false;
          }
          // Reset initialized flag so init() can run again when this
          // variant is re-selected. init() will be called directly at
          // that point — inView won't re-fire on a connected element.
          this.previousGalleryEl.initialized = false;
        } catch (e) {
          console.warn(
            '[SplPdpExampleGallery] Error destroying previous Swiper instance.',
            e
          );
        }
      }

      // Steps 2–3: Hide all, then show the new instance.
      this.hideAll();
      this.showInstance(newInstance);

      // Step 4: Initialize the new MediaGallery directly.
      // init() is guarded by this.initialized — safe to call on an already-
      // initialized instance (no-op). For deferred instances being shown for
      // the first time, this triggers the full Swiper + zoom setup.
      if (newGalleryEl) {
        try {
          if (typeof newGalleryEl.init === 'function') {
            newGalleryEl.init();
          }
        } catch (e) {
          console.warn(
            '[SplPdpExampleGallery] Error initializing new MediaGallery instance.',
            e
          );
        }

        // Step 5: Safety patch — suppress setActiveMedia on the new gallery.
        // setActiveMedia already exits early when variant.featured_media is null
        // (always the case for metaobject variants). This patch prevents regression
        // if that early-exit behavior changes in a future theme update.
        this.patchSetActiveMedia(newGalleryEl);
      }

      // Step 6: Update state.
      this.activeVariantId = newVariantId;
      this.previousGalleryEl = newGalleryEl || null;
    }

    /**
     * hideAll()
     *
     * Hides all .spl-gallery-instance elements and restores inert + aria-hidden.
     * Called before showInstance() to ensure exactly one gallery is accessible
     * at any time.
     *
     * inert removes all descendant elements from the accessibility tree,
     * prevents focus traversal into the element, and prevents pointer events.
     * Combined with aria-hidden="true", screen readers see zero hidden galleries.
     */
    hideAll() {
      const allInstances = this.galleryContainer.querySelectorAll(
        '.spl-gallery-instance'
      );
      allInstances.forEach((instance) => {
        instance.classList.add('hidden');
        instance.setAttribute('inert', '');
        instance.setAttribute('aria-hidden', 'true');
      });
    }

    /**
     * showInstance(instanceEl)
     *
     * Makes a .spl-gallery-instance visible and fully accessible.
     * Removes hidden class, inert attribute, and aria-hidden.
     * Always called after hideAll() so only this instance is active.
     *
     * @param {Element} instanceEl
     */
    showInstance(instanceEl) {
      instanceEl.classList.remove('hidden');
      instanceEl.removeAttribute('inert');
      instanceEl.removeAttribute('aria-hidden');
    }
    /**
     * patchSetActiveMedia(galleryEl)
     *
     * Replaces setActiveMedia() on a specific <media-gallery> instance with a
     * no-op. This is a safety measure against product-info.js's updateMedia()
     * call which runs setActiveMedia(variant) after each variant change fetch.
     *
     * In practice this patch is not needed for metaobject-driven galleries:
     * setActiveMedia() in media-gallery.js already exits early when
     * variant.featured_media is null — which is always the case for variants
     * whose images come from the Examples metaobject rather than Shopify's
     * native product media. The patch prevents regression if that early-exit
     * behavior changes in a future theme update.
     *
     * Why patch the instance rather than the prototype:
     *   Patching MediaGallery.prototype.setActiveMedia would affect ALL
     *   media-gallery elements on the page, including any non-SPL galleries
     *   (e.g. quick view modals using native Shopify product media).
     *   Instance-level patching is scoped to only this specific element.
     *
     * The patch is re-applied on every swap because stamped <template> clones
     * produce new DOM elements with the original prototype method intact —
     * instance patches do not survive cloneNode().
     *
     * @param {Element} galleryEl - The <media-gallery> element to patch.
     */
    patchSetActiveMedia(galleryEl) {
      if (!galleryEl) return;
      galleryEl.setActiveMedia = function () {
        // Intentional no-op.
        // Metaobject images have no variant.featured_media — setActiveMedia()
        // already exits early in this case. Gallery swap and slide reset are
        // handled entirely by SplPdpExampleGallery.swapGallery().
      };
    }

    /**
     * destroy()
     *
     * Removes the pubsub subscription and nulls all references.
     * Called by the MutationObserver in bootstrap() when the product-info
     * element is removed from the DOM (e.g. quick view close, product swap).
     *
     * Does not explicitly destroy Swiper instances — MediaGallery handles
     * its own cleanup when its element is removed from the DOM.
     */
    destroy() {
      if (typeof this.unsubscribeVariantChange === 'function') {
        this.unsubscribeVariantChange();
      }
      this.productInfoEl = null;
      this.galleryContainer = null;
      this.previousGalleryEl = null;
      this.unsubscribeVariantChange = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Bootstrap
  //
  // Finds all product-info elements on the page that have SPL gallery instances
  // and creates one SplPdpExampleGallery controller per element.
  //
  // Why we target product-info elements with .spl-gallery-instance children only:
  //   Quick view modals also use <product-info> but render native product.media
  //   galleries via a different snippet — they will not have .spl-gallery-instance
  //   children and are safely excluded by the hasSplGallery check below.
  //   This means quick view galleries continue to use the native setActiveMedia()
  //   behavior without interference from this controller.
  //
  // Why DOMContentLoaded rather than a custom element connectedCallback:
  //   SplPdpExampleGallery is a controller class, not a custom element. It wraps
  //   an existing custom element (product-info) and coordinates between it and
  //   the gallery DOM. Bootstrapping here keeps it decoupled from the theme's
  //   custom element registry and avoids timing issues with connectedCallback
  //   firing before sibling elements (the gallery instances) are in the DOM.
  //
  // Why variantChange subscription timing is safe:
  //   FoxTheme.pubsub is a synchronous pub/sub system initialized in theme.js
  //   before any section scripts run. By the time this deferred script executes,
  //   FoxTheme.pubsub is guaranteed to be available. variantChange events are
  //   only published after user interaction, so there is no risk of missing an
  //   event during bootstrap.
  // ---------------------------------------------------------------------------

  function bootstrap() {
    const productInfoElements = document.querySelectorAll('product-info');

    productInfoElements.forEach((productInfoEl) => {
      const sectionId = productInfoEl.dataset.section;
      if (!sectionId) return;

      // Scope the gallery container search to the section wrapper.
      const section = productInfoEl.closest(
        `#shopify-section-${sectionId}`
      ) || document;

      const galleryContainer = section.querySelector('.product__media-wrapper');
      if (!galleryContainer) return;

      // Only initialize for product-info elements that have SPL gallery instances.
      // Quick view and other non-SPL contexts are excluded here automatically.
      const hasSplGallery = galleryContainer.querySelector('.spl-gallery-instance');
      if (!hasSplGallery) return;

      // Create the controller. Constructor calls init() internally.
      const controller = new SplPdpExampleGallery(productInfoEl);

      // Expose on the element for console debugging:
      //   document.querySelector('product-info').splGallery
      productInfoEl.splGallery = controller;

      // Clean up when the product-info element is removed from the DOM.
      // subtree: false is sufficient — product-info is a direct child of its
      // parent, so childList on the immediate parent catches removal correctly
      // without the performance cost of observing the entire subtree.
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const removedNode of mutation.removedNodes) {
            if (
              removedNode === productInfoEl ||
              removedNode.contains?.(productInfoEl)
            ) {
              controller.destroy();
              observer.disconnect();
              return;
            }
          }
        }
      });

      const observeTarget = productInfoEl.parentElement || document.body;
      observer.observe(observeTarget, { childList: true, subtree: false });
    });
  }

  // Run bootstrap after DOM is ready.
  // If this deferred script executes after DOMContentLoaded has already fired
  // (common on fast connections), run immediately.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

})();
